import type { StyleSpecification } from "maplibre-gl";
import { layers, namedFlavor } from "@protomaps/basemaps";

// Protomaps' hosted glyphs + sprites. To go fully self-hosted for offline, copy
// https://github.com/protomaps/basemaps-assets into public/basemaps-assets and
// point these at import.meta.env.BASE_URL.
const ASSETS = import.meta.env.VITE_BASEMAP_ASSETS || "https://protomaps.github.io/basemaps-assets";

export function buildStyle(theme: "light" | "dark", tilesUrl: string): StyleSpecification {
  const flavorName = theme === "dark" ? "dark" : "light";
  const flavor = namedFlavor(flavorName);
  const base = layers("protomaps", flavor, { lang: "en" });

  // Small tweaks for a driving/trip-planning map: slightly stronger minor roads and
  // a calmer building tone so our markers stay the loudest thing on screen.
  for (const l of base) {
    // Outside the Ireland extract there are no tiles at all; paint that as sea rather
    // than the flavor's land-grey so the Atlantic edge of the map doesn't look broken.
    if (l.id === "background" && l.paint) {
      (l.paint as Record<string, unknown>)["background-color"] = flavor.water;
    }
    if (l.type === "line" && /^roads_(minor|minor_service)$/.test(l.id) && l.paint) {
      (l.paint as Record<string, unknown>)["line-opacity"] = 0.95;
    }
    if (l.id === "buildings" && l.paint) {
      (l.paint as Record<string, unknown>)["fill-opacity"] = theme === "dark" ? 0.35 : 0.5;
    }
    // This is a map of Ireland: a big "IRELAND" across the midlands (and "UNITED
    // KINGDOM" off the edge) says nothing and takes the space our labels want.
    if (l.id === "places_country") {
      l.layout = { ...(l.layout ?? {}), visibility: "none" };
    }
  }

  return {
    version: 8,
    glyphs: `${ASSETS}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${ASSETS}/sprites/v4/${flavorName}`,
    sources: {
      protomaps: {
        type: "vector",
        url: "pmtiles://" + tilesUrl,
        attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> · <a href="https://protomaps.com" target="_blank" rel="noopener">Protomaps</a>',
      },
    },
    layers: base,
  };
}
